import { pushFaroEvent } from '@utils/faro';
import {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { IPaymentMethodsQueryResult, PaymentMethod } from '@monofront/types';
import { GET_CURRENT_PAYMENT_METHODS } from '@monofront/business/queries';
import logger from '@utils/logger';
import { useAuthentication } from '@context/businessContainer';
import invariant from 'tiny-invariant';

interface IPaymentMethodContext {
  paymentMethods: PaymentMethod[];
  isLoading: boolean;
  refreshList: () => Promise<void>;
  error?: ApolloError;
}

/**
 * This Context returns the state of the customer's saved payment methods.
 * @returns paymentMethods (array): data from the request
 * @returns isLoading (boolean)
 * @returns refreshList (function)
 * @returns error (AppoloError)
 */
const PaymentMethodContext = createContext<IPaymentMethodContext | null>(null);

/**
 * This Provider fetches and handles the customer's saved payment methods.
 */
const PaymentMethodProvider = ({ children }) => {
  const {
    loading: firstFetchIsLoading,
    data: paymentMethodsQuery,
    refetch: refetchPaymentMethodsQuery,
    error,
  } = useQuery<IPaymentMethodsQueryResult>(GET_CURRENT_PAYMENT_METHODS, {
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });
  const { authUser } = useAuthentication();
  const [refetchIsLoading, setRefetchIsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  const refreshList = useCallback(async () => {
    setRefetchIsLoading(true);

    const { data: paymentMethodsRefetchQuery } =
      await refetchPaymentMethodsQuery();

    setPaymentMethods(
      paymentMethodsRefetchQuery?.getCurrentPaymentMethods || [],
    );

    setRefetchIsLoading(false);
  }, [refetchPaymentMethodsQuery]);

  useEffect(() => {
    setPaymentMethods(paymentMethodsQuery?.getCurrentPaymentMethods || []);
  }, [paymentMethodsQuery?.getCurrentPaymentMethods]);

  useEffect(() => {
    if (error) {
      logger.error('[payment] Cannot fetch existing payment method', {
        user_id: authUser?.uid,
        error,
      });
      pushFaroEvent('payment-method-fetch-failed');
    }
  }, [error]);

  return (
    <PaymentMethodContext.Provider
      value={useMemo(
        () => ({
          paymentMethods,
          isLoading: firstFetchIsLoading || refetchIsLoading,
          refreshList,
          error,
        }),
        [paymentMethods, firstFetchIsLoading, refetchIsLoading, error],
      )}
    >
      {children}
    </PaymentMethodContext.Provider>
  );
};

const usePaymentMethods = () => {
  const ctx = useContext(PaymentMethodContext);

  if (!ctx) {
    invariant(
      ctx,
      'usePaymentMethods must be used within a PaymentMethodProvider',
    );
  }

  return ctx;
};

export { PaymentMethodProvider, PaymentMethodContext, usePaymentMethods };
